import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import shortid from 'shortid'
import classNames from 'classnames'
import numeral from 'numeraljs'
import moment from 'moment'

import Input, { Label, PassiveInput } from './input'
import Button, { ButtonRow } from './button'
import { copyString, getItemByKeyValue } from '../helpers'
import styles from '../component-styles/campaign-code-generator-results.module.scss'
import { OuterBox, InnerBox } from './two-columns'
import { Box } from './tooltip'
import { CampaignCodesCompressed } from '../api-client/api-types'
import ShareCampaignCodesButton from './share-campaign-codes-button'
import { downloadSpecificCodes } from '../api-client/track-client'
import { getCsvString } from '../helpers/campaign-codes'
import { deleteCampaignCodesAsync } from '../store/actions'
import DeleteButtonWithConfirmation from './delete-button-with-confirmation'
import { TableLink } from './links-table'
import CreateQrModal from './create-qr-modal'
import { Preloader } from './loader'

const YESTERDAY = moment().subtract(1, 'days').startOf('day')

interface CampaignCodesInternal {
  item: CampaignCodesCompressed[]
  time: string
}

function getMutliCodesToCopy(paramDefs: any, item: CampaignCodesCompressed[]) {
  if (item.length === 0) {
    return { code: '', all: [] }
  }
  const hasShortLink = item.some((i) => i.sL !== '')

  const copyHeader = [
    hasShortLink ? 'Short link' : null,
    'Campaign code',
    ...item[0].cDef
      .map((i) => {
        const f = getItemByKeyValue(paramDefs, 'fieldID', i.fID)
        return f === -1 || i.n === '' ? '' : f.fieldName
      })
      .filter((i) => i !== ''),
  ]
    .filter((i) => i !== null)
    .join('\t')

  const code = item.map(({ c, sL, cDef }) => {
    const shortLink = sL && sL !== '' ? `https://upl.inc/${sL}` : ''
    return [
      hasShortLink ? shortLink : null,
      c,
      cDef
        .map((i) => {
          const f = getItemByKeyValue(paramDefs, 'fieldID', i.fID)
          if (f !== -1 && i.n !== '') {
            return i.n
          }
          return ''
        })
        .filter((i) => i !== '')
        .join('\t'),
    ]
      .filter((i) => i !== null)
      .join('\t')
  })
  return { code, all: [copyHeader, ...code] }
}

function getOutputMultiCodesToCopy(item: any[]) {
  if (item.length === 0) {
    return { code: '', all: [] }
  }

  const copyHeader = [
    'Campaign code',
    ...item[0].pDfs
      .map((i) => {
        return i[0] === '' ? '' : i[2]
      })
      .filter((i) => i !== ''),
  ].join('\t')

  const generatedCodes = item.map(({ code, pDfs }) => {
    return [
      code,
      pDfs
        .map((i) => {
          return i[0]
        })
        .filter((i) => i !== '')
        .join('\t'),
    ].join('\t')
  })
  return { code: generatedCodes, all: [copyHeader, ...generatedCodes] }
}

export function Output({
  single,
  generatedCodes,
}: {
  single: boolean
  generatedCodes: string | any[]
}): React.ReactElement {
  if (single) {
    return (
      <>
        <PassiveInput
          id="generated-code"
          value={typeof generatedCodes === 'string' ? generatedCodes : ''}
        />
        <Button
          type="text"
          align="left"
          onClick={(): void => copyString(generatedCodes)}
        >
          Copy
        </Button>
      </>
    )
  }

  const { code, all: toCopy } = getOutputMultiCodesToCopy(
    typeof generatedCodes === 'string' ? [] : generatedCodes,
  )

  return (
    <>
      <div className={classNames(styles.codeListItem, styles.codeMultipleList)}>
        {typeof code !== 'string' && code.length > 0 ? (
          <>{numeral(code.length).format('0,0')} multiple codes created</>
        ) : (
          <span className={styles.empty} />
        )}
      </div>
      <Button type="text" align="left" onClick={(): void => copyString(toCopy)}>
        Copy
      </Button>
    </>
  )
}

interface PreviousCodesListProps {
  grouped: any[]
  generatedCodes: string | string[]
  generatedStructure: any
  onChecked: (cID: string[], status: boolean) => void
  checkedFields: string[]
  loading: boolean
  className?: string
  totalCodes: number
  setShowQrModal: (state: boolean) => void
  setSelectedCodes: (codes: string[]) => void
}

export function PreviousCodesList({
  loading,
  grouped,
  generatedCodes,
  generatedStructure,
  onChecked,
  checkedFields,
  className,
  totalCodes,
  setShowQrModal,
  setSelectedCodes,
}: PreviousCodesListProps): React.ReactElement {
  const dispatch = useDispatch()
  const [active, setActive] = useState('')
  const [codesCounter, setCodesCounter] = useState<null | number>(null)
  const [animatedClass, setAnimatedClass] = useState('')

  const { separator, prefix } = useMemo(() => {
    if (generatedStructure && generatedStructure.paramSeparator) {
      const { paramSeparator, masterPrefix } = generatedStructure
      return { separator: paramSeparator, prefix: masterPrefix }
    }
    return { separator: '&', prefix: '?' }
  }, [generatedStructure])

  const firstCreatedNow =
    (Array.isArray(generatedCodes) &&
      grouped.length > 0 &&
      generatedCodes.length === grouped[0].item.length) ||
    (typeof generatedCodes === 'string' &&
      grouped.length > 0 &&
      grouped[0].item &&
      grouped[0].item[0].c === generatedCodes)

  useEffect(() => {
    if (
      codesCounter === null ||
      (codesCounter !== null && codesCounter !== totalCodes)
    ) {
      setCodesCounter(totalCodes)
      if (firstCreatedNow) {
        setAnimatedClass(styles.animatedFirstLi)
        window.setTimeout(() => {
          setAnimatedClass(styles.animatedFirstLiFinished)
        }, 1200)
      }
    }
  }, [totalCodes])

  return (
    <>
      <Label heading>Your recently created links</Label>
      {loading ? (
        <Preloader
          style={{ width: 75, height: 50, marginTop: 60, marginBottom: 60 }}
        />
      ) : (
        <ul className={classNames(className, animatedClass, styles.codeList)}>
          {grouped.map(
            (
              group: CampaignCodesInternal,
              index: number,
            ): React.ReactElement | null => {
              const { item, time } = group
              const key = shortid.generate()
              let showTime = ''
              const mT = moment(time)
              const days = moment().diff(mT, 'days')
              const isYesterday = mT.isSame(YESTERDAY, 'd')
              if (firstCreatedNow && index === 0) {
                showTime = 'just now'
              } else if (isYesterday) {
                showTime = 'yesterday'
              } else if (days === 0) {
                showTime = `at ${mT.format('h:mma')}`
              } else {
                showTime = `${mT.format('ddd Do, h:mma')}`
              }
              if (item.length === 1) {
                let isSingleChecked = true
                const cID = item[0].cID ? item[0].cID : null
                if (cID && checkedFields.indexOf(cID) === -1) {
                  isSingleChecked = false
                }

                const displayLink = item[0].c.replace(
                  /(^https:\/\/www\.)|(^https:\/\/)|(^http:\/\/)/i,
                  '',
                )
                return (
                  <li
                    key={key}
                    className={classNames(styles.codeListItem, {
                      [styles.justNow]: showTime === 'just now',
                    })}
                    onMouseLeave={(): void => setActive('')}
                  >
                    <div className={styles.showRow}>
                      {active === item[0].cID && (
                        <Box
                          className={styles.box}
                          message={
                            <TableLink
                              className={styles.tableLink}
                              active={false}
                              prefix={prefix}
                              separator={separator}
                              campaignLink={item[0].c}
                              showHighlight
                            />
                          }
                        />
                      )}
                      <Input
                        type="checkbox"
                        id={key}
                        name="selectItem"
                        checked={isSingleChecked}
                        className={styles.selectItem}
                        label=" "
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>,
                        ): void => {
                          const { checked } = e.target as HTMLInputElement
                          if (cID) {
                            onChecked([cID], checked)
                          }
                        }}
                      />
                      <div
                        className={styles.hoverable}
                        onMouseOver={(): void => setActive(item[0].cID || '')}
                        onMouseLeave={(): void => setActive('')}
                        onFocus={(): void => setActive(item[0].cID || '')}
                        onBlur={(): void => setActive('')}
                      >
                        {item[0].sL === '' ? (
                          <span className={styles.linkCode}>{displayLink}</span>
                        ) : (
                          <span className={styles.linkCode}>
                            https://upl.inc/{item[0].sL}
                          </span>
                        )}
                      </div>
                      <div className={styles.inlineDate}>
                        <span>{showTime}</span>
                      </div>
                    </div>
                    <Button
                      className={styles.qrButton}
                      type="qrCode"
                      align="left"
                      onClick={(): void => {
                        setSelectedCodes([item[0].cID || ''])
                        setShowQrModal(true)
                      }}
                    />
                    <Button
                      className={styles.copyButton}
                      type="copy"
                      align="left"
                      onClick={(): void => {
                        const copyLink =
                          item[0].sL === ''
                            ? item[0].c
                            : `https://upl.inc/${item[0].sL}`
                        copyString(copyLink)
                      }}
                    />
                    <DeleteButtonWithConfirmation
                      className={styles.deleteButtons}
                      onClick={(): void => {
                        dispatch(deleteCampaignCodesAsync([item[0].cID]))
                      }}
                    >
                      <p>Delete link?</p>
                    </DeleteButtonWithConfirmation>
                  </li>
                )
              }
              const { code, all: toCopy } = getMutliCodesToCopy(
                generatedStructure.paramDefs,
                item,
              )
              // At least one item in the list needs to be checked
              const checkedItems = item.filter(
                (i) => i.cID && checkedFields.indexOf(i.cID) !== -1,
              )
              const isMultipleChecked = checkedItems.length > 0
              return (
                <li
                  key={key}
                  className={classNames(
                    styles.codeListItem,
                    styles.codeMultipleList,
                    {
                      [styles.justNow]: showTime === 'just now',
                    },
                  )}
                >
                  <div className={styles.showRow}>
                    <Input
                      type="checkbox"
                      id={key}
                      name="selectItem"
                      checked={isMultipleChecked}
                      className={styles.selectItem}
                      label=" "
                      onChange={(
                        e: React.ChangeEvent<HTMLInputElement>,
                      ): void => {
                        const { checked } = e.target as HTMLInputElement
                        const cIDs = item
                          .map((i) => {
                            if (i.cID) {
                              return i.cID
                            }
                            return ''
                          })
                          .filter((c) => c !== '')
                        onChecked(cIDs, checked)
                      }}
                    />
                    <span className={styles.linkCode}>
                      {numeral(code.length).format('0,0')} codes created{' '}
                    </span>
                    <div className={styles.inlineDate}>
                      <span>{showTime}</span>
                    </div>
                  </div>
                  <Button
                    className={styles.copyButton}
                    type="copy"
                    align="left"
                    onClick={(): void => copyString(toCopy)}
                  />
                  <DeleteButtonWithConfirmation
                    className={styles.deleteButtons}
                    onClick={(): void => {
                      const toDelete = item.map((i) => i.cID)
                      dispatch(deleteCampaignCodesAsync(toDelete))
                    }}
                  >
                    <p>Delete {item.length} links?</p>
                  </DeleteButtonWithConfirmation>
                </li>
              )
            },
          )}
        </ul>
      )}
    </>
  )
}

interface Props {
  generatedCodes: string | any[]
  codes: CampaignCodesCompressed[]
  single?: boolean
  generatedStructure: any
  loading: boolean
}

export default function GeneratedCodeResults({
  generatedCodes,
  codes,
  single = true,
  generatedStructure,
  loading,
}: Props): React.ReactElement | null {
  const [allCurrentCodeIds, setAllCurrentCodeIds] = useState<string[]>([])
  const [checkedFields, setCheckedFields] = useState<string[]>([])
  const [animatedClass, setAnimatedClass] = useState('')
  const [selectedCodes, setSelectedCodes] = useState<string[]>([])
  const [mounted, setMounted] = useState(false)
  const [showQrModal, setShowQrModal] = useState(false)
  const showTopBox = false

  useEffect(() => {
    if (
      (typeof generatedCodes === 'string' && generatedCodes !== '') ||
      (Array.isArray(generatedCodes) && generatedCodes.length > 0)
    ) {
      setAnimatedClass(styles.animated)
      window.setTimeout(() => {
        setAnimatedClass('')
      }, 1000)
      // setCheckedFields(allCodeIds)
    }
  }, [generatedCodes])

  const grouped = useMemo(() => {
    if (codes.length === 0) {
      return []
    }
    const chunked = {}
    codes.forEach((item) => {
      const { t } = item
      const key = t.slice(0, 19) // remove seconds
      if (Object.prototype.hasOwnProperty.call(chunked, key)) {
        chunked[key] = { item: [...chunked[key].item, item], time: t }
      } else {
        chunked[key] = { item: [item], time: t }
      }
    })
    const codesOrdered: any[] = []
    Object.keys(chunked)
      .slice(0, 10)
      .forEach((key: string) => {
        codesOrdered.push(chunked[key])
      })

    const allCodeIds: string[] = []
    codesOrdered.forEach(({ item }) => {
      item.forEach((i) => {
        if (i.cID) {
          allCodeIds.push(i.cID)
        }
      })
    })

    if (!mounted) {
      setMounted(true)
      setCheckedFields(allCodeIds)
      setAllCurrentCodeIds(allCodeIds)
    } else {
      // eslint-disable-next-line
      // debugger

      const newIn = allCodeIds.filter(
        (i) => allCurrentCodeIds.indexOf(i) === -1,
      )
      const oldOut = allCurrentCodeIds.filter(
        (i) => allCodeIds.indexOf(i) === -1,
      )
      if (newIn.length > 0 || oldOut.length > 0) {
        const newFields = [
          ...checkedFields.filter((i) => oldOut.indexOf(i) === -1),
          ...newIn,
        ]
        setCheckedFields(newFields)
        setAllCurrentCodeIds(allCodeIds)
      }
    }

    return codesOrdered
  }, [codes])

  return (
    <OuterBox>
      {showTopBox && (
        <InnerBox className={animatedClass} showDivider>
          <Label id="generated-code" heading>
            Your landing page with campaign {single ? 'code' : 'codes'}
          </Label>
          <Output single={single} generatedCodes={generatedCodes} />
        </InnerBox>
      )}
      <InnerBox>
        <PreviousCodesList
          setSelectedCodes={(state) => {
            setSelectedCodes(state)
          }}
          setShowQrModal={(state) => {
            setShowQrModal(state)
          }}
          totalCodes={codes.length}
          loading={loading}
          checkedFields={checkedFields}
          onChecked={(cIDs, checked) => {
            // eslint-disable-next-line
            // debugger
            let useFields = [...checkedFields]
            cIDs.forEach((item) => {
              if (checked) {
                if (checkedFields.indexOf(item) === -1) {
                  useFields = [...useFields, item]
                }
              } else {
                useFields = useFields.filter((cID) => !(item === cID))
              }
            })
            setCheckedFields(useFields)
          }}
          grouped={grouped}
          generatedCodes={generatedCodes}
          generatedStructure={generatedStructure}
        />
        <ButtonRow style={{ justifyContent: 'space-between' }} centerAlign>
          <Button type="text" align="left" to="/track/view-campaign-codes">
            View all links
          </Button>
          <span className={styles.buttonWrapper}>
            <Button
              type="flatPink"
              disabled={!(loading || (!loading && checkedFields.length > 0))}
              onClick={async () => {
                const csv = getCsvString({
                  generatedStructure,
                  selectedCodes: checkedFields,
                  codes,
                })
                await downloadSpecificCodes(csv)
              }}
            >
              Download
            </Button>
            <ShareCampaignCodesButton
              shareUserCodes
              selectedCodes={checkedFields}
              showSummaryTable={false}
              isActive={loading || (!loading && checkedFields.length > 0)}
            />
          </span>
          {showQrModal && (
            <CreateQrModal
              section="create"
              setShowModal={setShowQrModal}
              codes={codes}
              selectedCodes={selectedCodes}
            />
          )}
        </ButtonRow>
      </InnerBox>
    </OuterBox>
  )
}
